import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, SparklesIcon, CalendarCheckIcon } from 'lucide-react';
import { SpaDoors } from './SpaDoors';
// Online booking — update this to the spa's real Calendly link
const CALENDLY_URL = 'https://calendly.com/wonderoflifewellness1/30min';
interface Props {
  onEnter: () => void;
}
const WELCOME_IMG = 'https://cdn.magicpatterns.com/uploads/aRS8CJSbGdHJA1c9Zo4fRt/ChatGPT_Image_Jun_1,_2026,_05_46_54_PM.png';
export function IntroScene({
  onEnter
}: Props) {
  const [doorsOpen, setDoorsOpen] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const openDoors = () => {
    if (doorsOpen) return;
    setDoorsOpen(true);
    setTimeout(() => setRevealed(true), 1500);
  };
  const stepInside = () => {
    if (leaving) return;
    setLeaving(true);
    setTimeout(() => onEnter(), 1000);
  };
  const sparkles = useMemo(() => Array.from({
    length: 20
  }).map((_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: 6 + Math.random() * 12,
    duration: 2.5 + Math.random() * 3,
    delay: Math.random() * 4
  })), []);
  const candles = useMemo(() => [{
    x: 28,
    y: 70,
    size: 90
  }, {
    x: 52,
    y: 72,
    size: 70
  }, {
    x: 70,
    y: 70,
    size: 80
  }], []);
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);
  return <motion.div initial={{
    opacity: 1
  }} animate={{
    opacity: leaving ? 0 : 1
  }} transition={{
    duration: 0.9,
    ease: 'easeInOut'
  }} className="fixed inset-0 z-[100] overflow-hidden bg-aubergine-900" data-id="element-94">
      <SpaDoors open={doorsOpen} overlay={<div className="text-center px-6 -translate-y-[16vh] md:-translate-y-[14vh]" data-id="element-96">
            <motion.h1 initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1
      }} className="font-serif text-5xl md:text-7xl text-aubergine-800 leading-[0.95]" data-id="element-97">
              Wonder <span className="italic text-gold-500" data-id="element-98">of</span> Life
            </motion.h1>
            <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: 0.3
      }} className="text-[10px] md:text-xs uppercase tracking-[0.4em] text-gold-500 mt-4" data-id="element-99">
              Relax · Rejuvenate · Celebrate You
            </motion.p>
            <motion.button initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.7
      }} onClick={openDoors} className="group mt-10 inline-flex items-center gap-3 px-10 py-4 rounded-full bg-aubergine-800 border border-gold-400/40 text-cream-50 text-[11px] uppercase tracking-luxe hover:bg-aubergine-700 transition-all duration-500 shadow-[0_8px_30px_rgba(58,31,77,0.4)]" data-id="element-100">
              Enter the Spa
              <ArrowRightIcon size={14} className="text-gold-300 group-hover:translate-x-1 transition-transform" data-id="element-101" />
            </motion.button>
          </div>} data-id="element-95">
        {/* Revealed welcome / sign-in scene — WHOLE photo visible (welcome sign + full table) */}
        <div className="absolute inset-0 flex items-center justify-center p-4 md:p-8" style={{
        backgroundColor: '#cabfae'
      }} data-id="element-102">
          <div className="relative flex items-center justify-center" style={{
          aspectRatio: '1024/819',
          maxHeight: '100%',
          maxWidth: '100%'
        }} data-id="element-103">
            <img src={WELCOME_IMG} alt="Wonder of Life Wellness Spa welcome area — boxwood hedge wall with neon Welcome sign and the sign-in book" className="w-full h-full object-contain" data-id="element-104" />

            {/* Invisible Hotspots for the buttons in the image */}
            {revealed && <>
                {/* Book Now Hotspot */}
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="absolute z-30 cursor-pointer" style={{
              left: '48%',
              top: '85%',
              width: '30%',
              height: '6%',
              transform: 'translate(-50%, -50%)'
            }} aria-label="Book Now" data-id="element-105" />

                {/* Explore The Spa Hotspot */}
                <button onClick={stepInside} className="absolute z-30 cursor-pointer" style={{
              left: '76.5%',
              top: '93%',
              width: '35%',
              height: '6%',
              transform: 'translate(-50%, -50%)'
            }} aria-label="Explore the Spa" data-id="element-106" />
              </>}

            {/* Candle glows over the table */}
            {candles.map((c, i) => <motion.div key={i} animate={{
            opacity: [0.4, 0.7, 0.45, 0.65, 0.4]
          }} transition={{
            duration: 2 + i * 0.4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute pointer-events-none rounded-full mix-blend-screen" style={{
            left: `${c.x}%`,
            top: `${c.y}%`,
            width: c.size,
            height: c.size,
            transform: 'translate(-50%, -50%)',
            background: 'radial-gradient(ellipse at center, rgba(255,220,150,0.55) 0%, rgba(255,190,110,0.2) 45%, transparent 70%)',
            filter: 'blur(10px)'
          }} data-id="element-107" />)}

            {/* Sparkles */}
            {sparkles.map(s => <motion.div key={s.id} animate={{
            opacity: [0, 1, 0],
            scale: [0.4, 1, 0.4]
          }} transition={{
            duration: s.duration,
            repeat: Infinity,
            delay: s.delay,
            ease: 'easeInOut'
          }} className="absolute pointer-events-none" style={{
            left: `${s.left}%`,
            top: `${s.top}%`
          }} data-id="element-108">
                <SparklesIcon size={s.size} className="text-gold-300" style={{
              filter: 'drop-shadow(0 0 6px rgba(224,196,126,0.8))'
            }} data-id="element-109" />
              </motion.div>)}
          </div>
        </div>
      </SpaDoors>
    </motion.div>;
}